import { Injectable, inject } from '@angular/core';
import moment from 'moment';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { Brand } from 'src/app/components/company/models/new/brand.model';
import { Category } from '../models/new/category';
import { Gender } from '../models/new/style';
import { StyleDescription } from '../models/new/style-description';
import { StyleGroup } from '../models/new/style-group';
import { Collection } from '../models/new/collection';
import { Purpose } from '../models/new/purpose';
import { Team } from '../models/new/team';
import { Status } from '../models/new/status';
import { PriceChange, StylePrice, StylePriceFilters } from '../models/new/style-price';
import { Order } from '../models/new/order';
import _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class StylePriceMapper {
  #utils = inject(UtilitiesService);

  toStylePrice(_style: any): StylePrice {
    return new StylePrice({
      brand: new Brand({
        id: _style.brand.id,
        name: _style.brand.name
      }),
      category: new Category({
        id: _style.category.id,
        name: _style.category.name
      }),

      modelId: _style.modelId,
      code: _style.code,
      name: _style.name,
      styleGroup: _style.styleGroup
        ? new StyleGroup({
            id: _style.styleGroup.id,
            name: _style.styleGroup.name
          })
        : undefined,
      styleDescription: _style.styleDescription
        ? new StyleDescription({
            id: _style.styleDescription.id,
            name: _style.styleDescription.name
          })
        : undefined,
      gender: _style.gender
        ? new Gender({
            id: _style.gender.id,
            name: _style.gender.name.toLowerCase().replace(/^\w/, (c: string) => c.toUpperCase())
          })
        : undefined,

      priceChanges:
        _style.priceChanges?.map(
          (change: any) =>
            new PriceChange({
              orderId: change.orderId,
              orderCreationDate: new Date(change.orderCreationDate),
              price: change.price
            })
        ) || [],
      periodAverage: _style.periodAverage
    });
  }

  filtersToUrl(filters: StylePriceFilters) {
    return {
      brands: filters.brands ? filters.brands.map((brand) => brand.id) : undefined,
      categories: filters.categories ? filters.categories.map((category) => category.id) : undefined,
      styleGroups: filters.styleGroups ? filters.styleGroups.map((styleGroup) => styleGroup.id) : undefined,
      styleDescriptions: filters.styleDescriptions
        ? filters.styleDescriptions.map((styleDescription) => styleDescription.id)
        : undefined,
      collections: filters.collections ? filters.collections.map((collection) => collection.id) : undefined,
      purposes: filters.purposes ? filters.purposes.map((purpose) => purpose.id) : undefined,
      genders: filters.genders ? filters.genders.map((gender) => gender.id) : undefined,
      teams: filters.teams ? filters.teams.map((team) => team.id) : undefined,
      styleCode: filters.styleCode || undefined,
      orderIds: filters.orderIds?.length ? filters.orderIds : undefined,
      basicNetIds: filters.basicNetIds?.length ? filters.basicNetIds : undefined,
      statuses: filters.statuses ? filters.statuses.map((status) => status.id) : undefined,
      dateFrom: filters.dateRange?.from ? moment(filters.dateRange.from).format('YYYY-MM-DD') : undefined,
      dateTo: filters.dateRange?.to ? moment(filters.dateRange.to).format('YYYY-MM-DD') : undefined,
      page: filters.page || 1,
      size: filters.size || 20
    };
  }

  urlToFilters(url: any): StylePriceFilters {
    return new StylePriceFilters({
      brands: url.brands
        ? Array.isArray(url.brands)
          ? url.brands.map((id: any) => new Brand({ id: Number(id) }))
          : [new Brand({ id: Number(url.brands) })]
        : undefined,
      categories: url.categories
        ? Array.isArray(url.categories)
          ? url.categories.map((id: any) => new Category({ id: Number(id) }))
          : [new Category({ id: Number(url.categories) })]
        : undefined,
      styleGroups: url.styleGroups
        ? Array.isArray(url.styleGroups)
          ? url.styleGroups.map((id: any) => new StyleGroup({ id: Number(id) }))
          : [new StyleGroup({ id: Number(url.styleGroups) })]
        : undefined,
      styleDescriptions: url.styleDescriptions
        ? Array.isArray(url.styleDescriptions)
          ? url.styleDescriptions.map((id: any) => new StyleDescription({ id: Number(id) }))
          : [new StyleDescription({ id: Number(url.styleDescriptions) })]
        : undefined,
      collections: url.collections
        ? Array.isArray(url.collections)
          ? url.collections.map((id: any) => new Collection({ id: Number(id) }))
          : [new Collection({ id: Number(url.collections) })]
        : undefined,
      purposes: url.purposes
        ? Array.isArray(url.purposes)
          ? url.purposes.map((id: any) => new Purpose({ id: Number(id) }))
          : [new Purpose({ id: Number(url.purposes) })]
        : undefined,
      genders: url.genders
        ? Array.isArray(url.genders)
          ? url.genders.map((id: any) => new Gender({ id: Number(id) }))
          : [new Gender({ id: Number(url.genders) })]
        : undefined,
      teams: url.teams
        ? Array.isArray(url.teams)
          ? url.teams.map((id: any) => new Team({ id: Number(id) }))
          : [new Team({ id: Number(url.teams) })]
        : undefined,
      statuses: url.statuses
        ? Array.isArray(url.statuses)
          ? url.statuses.map((id: any) => new Status({ id: Number(id) }))
          : [new Status({ id: Number(url.statuses) })]
        : undefined,
      styleCode: url.styleCode || undefined,
      orderIds: url.orderIds
        ? Array.isArray(url.orderIds)
          ? url.orderIds.map((id: any) => Number(id))
          : [Number(url.orderIds)]
        : undefined,
      basicNetIds: url.basicNetIds
        ? Array.isArray(url.basicNetIds)
          ? url.basicNetIds.map((id: any) => Number(id))
          : [Number(url.basicNetIds)]
        : undefined,
      dateRange:
        url.dateFrom || url.dateTo
          ? {
              from: url.dateFrom ? moment(url.dateFrom, 'YYYY-MM-DD').startOf('day').toDate() : undefined,
              to: url.dateTo ? moment(url.dateTo, 'YYYY-MM-DD').endOf('day').toDate() : undefined
            }
          : undefined,
      page: Number(url.page || 1),
      size: Number(url.size || 20)
    });
  }

  toWhereClause(filters: StylePriceFilters) {
    return {
      brand: filters.brands?.length ? { id: { in: filters.brands.map((b) => b.id) } } : undefined,
      category: filters.categories?.length ? { id: { in: filters.categories.map((c) => c.id) } } : undefined,
      code: filters.styleCode ? { contains: filters.styleCode } : undefined,
      styleGroup: filters.styleGroups?.length ? { id: { in: filters.styleGroups.map((sg) => sg.id) } } : undefined,
      styleDescription: filters.styleDescriptions?.length
        ? { id: { in: filters.styleDescriptions.map((sd) => sd.id) } }
        : undefined,
      ...(filters.collections?.length && {
        schema: { some: { collection: { id: { in: filters.collections.map((col) => col.id) } } } }
      }),
      ...(filters.teams?.length && {
        schema: { some: { schemaTeamColors: { some: { team: { id: { in: filters.teams.map((t) => t.id) } } } } } }
      }),
      purpose: filters.purposes?.length ? { id: { in: filters.purposes.map((p) => p.id) } } : undefined,
      gender: filters.genders?.length ? { id: { in: filters.genders.map((g) => g.id) } } : undefined,
      ...(filters.statuses?.length && {
        currentStyleStatus: { some: { styleStatus: { id: { in: filters.statuses.map((s) => s.id) } } } }
      }),
      // style presenti negli ordini indicati (order id e/o BasicNet ID, stessa riga d'ordine)
      ...((filters.orderIds?.length || filters.basicNetIds?.length) && {
        orderStyles: {
          some: {
            orderId: filters.orderIds?.length ? { in: filters.orderIds } : undefined,
            order: filters.basicNetIds?.length ? { basicNetId: { in: filters.basicNetIds } } : undefined
          }
        }
      })
    };
  }
}
